import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface Order {
  id: number;
  orderNo: string;
  orderRef: string;
  jobType: string;
  estimatedDate: string;
  shippedBy: string;
  message: string;
  description?: string;
  imageName?: string;
  status: string;
}

const JOB_TYPES = [
  { value: 'Monolithic Zirconia', label: 'Monolithic Zirconia (5 working days)', days: 5 },
  { value: 'Layered zirconia', label: 'Layered zirconia (7 working days)', days: 7 },
  { value: 'Porcelain bonded crown', label: 'Porcelain bonded crown (7 working days)', days: 7 },
  { value: 'Venirs', label: 'Venirs (10 working days)', days: 10 },
  { value: 'Emax', label: 'Emax (10 working days)', days: 10 },
  { value: 'Composite', label: 'Composite (7 working days)', days: 7 },
  { value: 'Temporary crown Pmma', label: 'Temporary crown Pmma (7 working days)', days: 7 },
  { value: 'Screw retained crown', label: 'Screw retained crown (7 working days)', days: 7 },
  { value: 'Splints', label: 'Splints (5 working days)', days: 5 },
  { value: 'Order', label: 'Order (to discuss)', days: 0 }
];

// Funcție pentru calcularea working days (exclude sâmbăta și duminica)
const addWorkingDays = (startDate: Date, workingDays: number): Date => {
  const result = new Date(startDate);
  let addedDays = 0;

  while (addedDays < workingDays) {
    result.setDate(result.getDate() + 1);
    // 0 = Duminică, 6 = Sâmbătă
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      addedDays++;
    }
  }

  return result;
};

const EditOrder = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // State-uri pentru formular
  const [orderRef, setOrderRef] = useState('');
  const [jobType, setJobType] = useState('');
  const [shippedBy, setShippedBy] = useState('');
  const [message, setMessage] = useState('');
  const [description, setDescription] = useState('');
  const [imageName, setImageName] = useState('');
  const [estimatedDate, setEstimatedDate] = useState<Date | null>(null);


  // State pentru file upload
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculează data estimată când se schimbă job type
  const calculateEstimatedDate = (jobTypeValue: string): Date | null => {
    const jobTypeInfo = JOB_TYPES.find(jt => jt.value === jobTypeValue);
    if (jobTypeInfo && jobTypeInfo.days > 0) {
      const currentDate = new Date();
      return addWorkingDays(currentDate, jobTypeInfo.days);
    }
    return null;
  };

  // Handler pentru schimbarea job type
  const handleJobTypeChange = (newJobType: string) => {
    setJobType(newJobType);
    const calculatedDate = calculateEstimatedDate(newJobType);
    if (calculatedDate) {
      setEstimatedDate(calculatedDate);
    }
  };

  const SHIPPING_OPTIONS = [
    { value: 'royal mail', label: 'Royal Mail' },
    { value: 'in person', label: 'In Person' }
  ];

  // Funcții pentru file upload
  const handleImageChange = (file: File) => {
    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      handleImageChange(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fetch comanda
  useEffect(() => {
    const fetchOrder = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in');
        navigate('/auth');
        return;
      }

      try {
        const res = await fetch(`https://felixdent-backend.onrender.com/api/orders/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          alert('Failed to fetch order');
          navigate('/my-orders');
          return;
        }

        const data: Order = await res.json();

        setOrder(data);
        setOrderRef(data.orderRef);
        setJobType(data.jobType);
        setShippedBy(data.shippedBy);
        setMessage(data.message);
        if (data.estimatedDate) {
          setEstimatedDate(new Date(data.estimatedDate));
        } else {
          setEstimatedDate(null);
        }
        setDescription(data.description || '');
        setImageName(data.imageName || '');
      } catch (error) {
        console.error('Error fetching order:', error);
        alert('Failed to fetch order data');
        navigate('/my-orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, navigate]);

  // Handler submit pentru update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!order) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in');
      navigate('/auth');
      return;
    }

    setSaving(true);

    // Create FormData pentru file upload
    const formData = new FormData();
    formData.append('orderRef', orderRef);
    formData.append('jobType', jobType);
    formData.append('estimatedDate', estimatedDate?.toISOString() || '');
    formData.append('shippedBy', shippedBy);
    formData.append('message', message);
    formData.append('description', description);

    if (imageFile) {
      formData.append('image', imageFile);
      formData.append('imageName', imageFile.name);
    }

    try {
      const res = await fetch(`https://felixdent-backend.onrender.com/api/orders/${order.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json();
        alert(`Error updating order: ${errData.message || res.statusText}`);
        return;
      }

      alert('Order updated successfully!');
      navigate('/my-orders');
    } catch (error) {
      alert('Network error. Please try again.');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#025951]"></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">Order Not Found</h1>
            <button
              onClick={() => navigate('/my-orders')}
              className="px-6 py-2 bg-[#025951] text-white rounded-lg hover:bg-[#034a43] transition-colors"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-white to-neutral-50 dark:from-neutral-800 dark:to-neutral-700 text-gray-900 dark:text-white">
        <div className="max-w-4xl mx-auto p-4 md:p-6">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Edit Order</h1>
              <p className="text-gray-600 dark:text-gray-400">Order #{order.orderNo}</p>
            </div>
            <button
              onClick={() => navigate('/my-orders')}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back to Orders
            </button>
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-neutral-700 rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-6 text-gray-800 dark:text-gray-200">Order Information</h2>

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Grid Layout pentru câmpuri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Order Reference */}
                <div>
                  <label htmlFor="orderRef" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Order Reference *
                  </label>
                  <input
                    id="orderRef"
                    type="text"
                    value={orderRef}
                    onChange={e => setOrderRef(e.target.value)}
                    required
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-600 text-gray-900 dark:text-white transition-colors"
                    placeholder="Enter order reference"
                  />
                </div>

                {/* Job Type - Dropdown */}
                <div>
                  <label htmlFor="jobType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Type *
                  </label>
                  <select
                    id="jobType"
                    value={jobType}
                    onChange={e => handleJobTypeChange(e.target.value)}
                    required
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-600 text-gray-900 dark:text-white transition-colors"
                  >
                    <option value="">Select job type</option>
                    {JOB_TYPES.map((jt) => (
                      <option key={jt.value} value={jt.value}>
                        {jt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Estimated Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estimated Completion Date *
                  </label>
                  <DatePicker
                    selected={estimatedDate}
                    onChange={(date) => setEstimatedDate(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select estimated date"
                    minDate={new Date()}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Auto-calculated as working days (excluding weekends)
                  </p>
                </div>

                {/* Shipped By */}
                <div>
                  <label htmlFor="shippedBy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Shipped By
                  </label>
                  <select
                    id="shippedBy"
                    value={shippedBy}
                    onChange={e => setShippedBy(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-600 text-gray-900 dark:text-white transition-colors"
                  >
                    {SHIPPING_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* File/Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload File/Image
                  </label>

                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleImageSelect}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="hidden"
                  />

                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 focus:outline-none focus:border-blue-500 transition-colors"
                    >
                      <div className="text-center">
                        <span className="text-2xl mb-2 block">📎</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Click to select file or image
                        </span>
                      </div>
                    </button>

                    {/* File Preview */}
                    {imageFile && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>📎</span>
                            <span className="text-sm font-medium">{imageFile.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(imageFile.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={removeImage}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>

                        {/* Image Preview */}
                        {imagePreview && imageFile.type.startsWith('image/') && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-w-full h-32 object-contain rounded border"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4}
                  className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-600 text-gray-900 dark:text-white transition-colors resize-none"
                  placeholder="Enter your message"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={4}
                  className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-600 text-gray-900 dark:text-white transition-colors resize-none"
                  placeholder="Enter detailed description"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#025951] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#034a43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-[#025951] focus:ring-offset-2"
                >
                  {saving ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving Changes...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/my-orders')}
                  className="flex-1 bg-gray-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-600 transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          {/* Info Card */}
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Job Types & Processing Times
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <p>• Estimated delivery dates are calculated automatically based on job type</p>
                  <p>• Order type requires manual discussion for timing</p>
                  <p>• Status updates are managed by admin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditOrder;